import personPlaceholder from 'images/placeholder/person_placeholder.png';
import {
  logActivitiesNavButtonTapped,
  logCreateMapNavButtonTapped,
  logExploreNavButtonClicked,
  logFavoritesNavButtonTapped,
  logHistoriesNavButtonTapped,
  logListsNavButtonTapped,
  logMapsNavButtonTapped,
  logStatsNavButtonTapped,
  logWebGlobalHeaderCommunityClicked
} from '@alltrails/analytics/amplitudeFunctions';
import { anonymizeUser } from '@alltrails/analytics/amplitudeHelpers';
import { LanguageSupportUtil } from './language_support_util';
import { StringConstantsMixin } from '../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { getPhotoUrl } from './photo_helper';

const { wrapUrlSafe } = LanguageSupportUtil;
const {
  strings: {
    DOWNLOAD_THE_APP,
    NAVIGATION: {
      LOGIN,
      SIGNUP,
      LOGOUT,
      SETTINGS,
      MEMBERS,
      PHOTOS,
      REVIEWS,
      PROFILE,
      ACTIVITIES,
      EXPLORE,
      MY_FAVORITES,
      LISTS,
      MY_MAPS,
      CREATE_MAP,
      COMMUNITY,
      COMPLETED,
      STATS,
      HELP,
      SAVED
    }
  }
} = StringConstantsMixin;

const explore = languageRegionCode => ({
  label: EXPLORE,
  linkPath: wrapUrlSafe('/explore?ref=header', languageRegionCode),
  onClick: logExploreNavButtonClicked
});

const amplitudeHeaderLink = (label, logAmplitudeEvent, linkPath, languageRegionCode, slug) => ({
  label,
  linkPath: slug ? wrapUrlSafe(linkPath, languageRegionCode) : wrapUrlSafe('/signup?ref=header', languageRegionCode),
  onClick: logAmplitudeEvent
});

const favorites = (languageRegionCode, slug) =>
  amplitudeHeaderLink(MY_FAVORITES, logFavoritesNavButtonTapped, `/members/${slug}/favorites?ref=header`, languageRegionCode, slug);

const lists = (languageRegionCode, slug) =>
  amplitudeHeaderLink(LISTS, logListsNavButtonTapped, `/members/${slug}/lists?ref=header`, languageRegionCode, slug);

const maps = (languageRegionCode, slug) =>
  amplitudeHeaderLink(MY_MAPS, logMapsNavButtonTapped, `/members/${slug}/maps?ref=header-plan-my-maps`, languageRegionCode, slug);

const createMap = (languageRegionCode, slug) =>
  amplitudeHeaderLink(CREATE_MAP, logCreateMapNavButtonTapped, '/explore/map/new', languageRegionCode, slug);

const activities = (languageRegionCode, slug) =>
  amplitudeHeaderLink(ACTIVITIES, logActivitiesNavButtonTapped, `/members/${slug}/recordings?ref=header`, languageRegionCode, slug);

const completed = (languageRegionCode, slug) =>
  amplitudeHeaderLink(COMPLETED, logHistoriesNavButtonTapped, `/members/${slug}/completed?ref=header`, languageRegionCode, slug);

const stats = (languageRegionCode, slug) =>
  amplitudeHeaderLink(STATS, logStatsNavButtonTapped, `/members/${slug}/stats?ref=header`, languageRegionCode, slug);

const loggedInProps = ({ firstName, slug, reputation, profilePhoto }, languageRegionCode = 'en-US', proUser) =>
  // use proUser from the context obj instead of "pro" from the currentUser object because private currentUser.pro is nil
  ({
    user: {
      name: firstName,
      linkPath: wrapUrlSafe(`/members/${slug}?ref=header`, languageRegionCode),
      imagePath:
        profilePhoto && profilePhoto.id && profilePhoto.photoHash ? getPhotoUrl(profilePhoto.id, profilePhoto.photoHash, 'icon') : personPlaceholder,
      reputation,
      display: true,
      login: {
        label: LOGIN,
        linkPath: wrapUrlSafe('/login?ref=header', languageRegionCode)
      },
      signup: {
        label: SIGNUP,
        linkPath: wrapUrlSafe('/signup?ref=header', languageRegionCode)
      },
      dropdown: [
        {
          label: PROFILE,
          linkPath: wrapUrlSafe(`/members/${slug}?ref=header`, languageRegionCode)
        },
        stats(languageRegionCode, slug),
        {
          label: REVIEWS,
          linkPath: wrapUrlSafe(`/members/${slug}/reviews?ref=header`, languageRegionCode)
        },
        {
          label: PHOTOS,
          linkPath: wrapUrlSafe(`/members/${slug}/photos?ref=header`, languageRegionCode)
        },
        {
          label: MEMBERS,
          linkPath: wrapUrlSafe('/members?ref=header', languageRegionCode)
        },
        {
          label: SETTINGS,
          linkPath: wrapUrlSafe('/my/profile/edit?ref=header', languageRegionCode)
        },
        {
          label: LOGOUT,
          linkPath: wrapUrlSafe('/sign_out?ref=header', languageRegionCode),
          onClick: anonymizeUser
        }
      ]
    },
    brand: {
      link: wrapUrlSafe('/?ref=header', languageRegionCode),
      proBadge: {
        linkPath: proUser ? wrapUrlSafe('/pro/welcome?ref=header', languageRegionCode) : wrapUrlSafe('/pro?ref=header', languageRegionCode)
      }
    },
    links: {
      explore: explore(languageRegionCode),
      saved: {
        label: SAVED,
        linkPath: wrapUrlSafe(`/members/${slug}/favorites?ref=header`, languageRegionCode),
        dropdown: [
          favorites(languageRegionCode, slug),
          maps(languageRegionCode, slug),
          lists(languageRegionCode, slug),
          activities(languageRegionCode, slug),
          completed(languageRegionCode, slug),
          createMap(languageRegionCode, slug)
        ]
      },
      help: {
        label: HELP,
        linkPath: `https://support.alltrails.com/hc/${languageRegionCode.split('-')[0]}`,
        locale: true
      },
      downloadApp: {
        label: DOWNLOAD_THE_APP,
        linkPath: 'https://alltrails.io/BrtlItRPbo'
      }
    }
  });
const loggedOutProps = (languageRegionCode = 'en-US') => {
  const signUpPath = wrapUrlSafe('/signup?ref=header', languageRegionCode);

  return {
    user: {
      display: false,
      login: {
        label: LOGIN,
        linkPath: wrapUrlSafe('/login?ref=header', languageRegionCode)
      },
      signup: {
        label: SIGNUP,
        linkPath: signUpPath
      },
      dropdown: []
    },
    brand: {
      link: wrapUrlSafe('/?ref=header', languageRegionCode),
      proBadge: {
        active: false,
        linkPath: wrapUrlSafe('/pro?ref=header', languageRegionCode)
      }
    },
    links: {
      explore: explore(languageRegionCode),
      saved: {
        label: SAVED,
        linkPath: wrapUrlSafe(`/members/slug/favorites?ref=header`, languageRegionCode),
        dropdown: [
          favorites(languageRegionCode),
          maps(languageRegionCode),
          lists(languageRegionCode),
          activities(languageRegionCode),
          completed(languageRegionCode),
          createMap(languageRegionCode)
        ]
      },
      help: {
        label: HELP,
        linkPath: `https://support.alltrails.com/hc/${languageRegionCode.split('-')[0]}`,
        locale: true
      },
      downloadApp: {
        label: DOWNLOAD_THE_APP,
        linkPath: 'https://alltrails.io/BrtlItRPbo'
      }
    }
  };
};

const communityLoggedInProps = ({ firstName, slug, reputation, profilePhoto }, languageRegionCode = 'en-US', proUser) => ({
  user: {
    name: firstName,
    linkPath: wrapUrlSafe(`/members/${slug}?ref=header`, languageRegionCode),
    imagePath:
      profilePhoto && profilePhoto.id && profilePhoto.photoHash ? getPhotoUrl(profilePhoto.id, profilePhoto.photoHash, 'icon') : personPlaceholder,
    reputation,
    display: true,
    login: {
      label: LOGIN,
      linkPath: wrapUrlSafe('/login?ref=header', languageRegionCode)
    },
    signup: {
      label: SIGNUP,
      linkPath: wrapUrlSafe('/signup?ref=header', languageRegionCode)
    },
    dropdown: [
      {
        label: PROFILE,
        linkPath: wrapUrlSafe(`/members/${slug}?ref=header`, languageRegionCode)
      },
      stats(languageRegionCode, slug),
      {
        label: REVIEWS,
        linkPath: wrapUrlSafe(`/members/${slug}/reviews?ref=header`, languageRegionCode)
      },
      {
        label: PHOTOS,
        linkPath: wrapUrlSafe(`/members/${slug}/photos?ref=header`, languageRegionCode)
      },
      {
        label: MEMBERS,
        linkPath: wrapUrlSafe('/members?ref=header', languageRegionCode)
      },
      {
        label: SETTINGS,
        linkPath: wrapUrlSafe('/my/profile/edit?ref=header', languageRegionCode)
      },
      {
        label: LOGOUT,
        linkPath: wrapUrlSafe('/sign_out?ref=header', languageRegionCode)
      }
    ]
  },
  brand: {
    link: wrapUrlSafe('/?ref=header', languageRegionCode),
    proBadge: {
      linkPath: proUser ? wrapUrlSafe('/pro/welcome?ref=header', languageRegionCode) : wrapUrlSafe('/pro?ref=header', languageRegionCode)
    }
  },
  links: {
    explore: explore(languageRegionCode),
    community: {
      label: COMMUNITY,
      linkPath: wrapUrlSafe('/community?ref=header', languageRegionCode),
      onClick: logWebGlobalHeaderCommunityClicked
    },
    saved: {
      label: SAVED,
      linkPath: wrapUrlSafe(`/members/${slug}/favorites?ref=header`, languageRegionCode),
      dropdown: [
        favorites(languageRegionCode, slug),
        maps(languageRegionCode, slug),
        lists(languageRegionCode, slug),
        activities(languageRegionCode, slug),
        completed(languageRegionCode, slug),
        createMap(languageRegionCode, slug)
      ]
    },
    help: {
      label: HELP,
      linkPath: `https://support.alltrails.com/hc/${languageRegionCode.split('-')[0]}`,
      locale: true
    },
    downloadApp: {
      label: DOWNLOAD_THE_APP,
      linkPath: 'https://alltrails.io/BrtlItRPbo'
    }
  }
});

const communityLoggedOutProps = languageRegionCode => {
  const signUpPath = wrapUrlSafe('/signup?ref=header', languageRegionCode);

  return {
    user: {
      display: false,
      login: {
        label: LOGIN,
        linkPath: wrapUrlSafe('/login?ref=header', languageRegionCode)
      },
      signup: {
        label: SIGNUP,
        linkPath: signUpPath
      },
      dropdown: []
    },
    brand: {
      link: wrapUrlSafe('/?ref=header', languageRegionCode),
      proBadge: {
        active: false,
        linkPath: wrapUrlSafe('/pro?ref=header', languageRegionCode)
      }
    },
    links: {
      explore: explore(languageRegionCode),
      community: {
        label: COMMUNITY,
        linkPath: signUpPath,
        onClick: logWebGlobalHeaderCommunityClicked
      },
      saved: {
        label: SAVED,
        linkPath: wrapUrlSafe(`/members/slug/favorites?ref=header`, languageRegionCode),
        dropdown: [
          favorites(languageRegionCode),
          maps(languageRegionCode),
          lists(languageRegionCode),
          activities(languageRegionCode),
          completed(languageRegionCode),
          createMap(languageRegionCode)
        ]
      },
      help: {
        label: HELP,
        linkPath: `https://support.alltrails.com/hc/${languageRegionCode.split('-')[0]}`,
        locale: true
      },
      downloadApp: {
        label: DOWNLOAD_THE_APP,
        linkPath: 'https://alltrails.io/BrtlItRPbo'
      }
    }
  };
};

const generateCommunityHeaderProps = (currentUser, languageRegionCode, proUser) =>
  currentUser ? communityLoggedInProps(currentUser, languageRegionCode, proUser) : communityLoggedOutProps(languageRegionCode);

const generateStandardHeaderProps = (currentUser, languageRegionCode, proUser) =>
  currentUser ? loggedInProps(currentUser, languageRegionCode, proUser) : loggedOutProps(languageRegionCode);

const generateNavigationProps = ({ currentUser, languageRegionCode, proUser, communityFeatureFlag = false }) =>
  communityFeatureFlag
    ? generateCommunityHeaderProps(currentUser, languageRegionCode, proUser)
    : generateStandardHeaderProps(currentUser, languageRegionCode, proUser);

export { generateNavigationProps };
